import { useEffect, useMemo, useRef, useState } from 'react';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { fetchEventCluster } from '@/pages/intelligence/services/eventCluster';
import { buildEventClusterParams } from './utils/buildOpinionTimelineParams';
import { mapEventClusterListToOpinionEventData } from './utils/mapEventClusterToOpinionEventData';
import { buildEventSettingSelectValue } from './utils/buildEventSettingSelectValue';
import { buildInitialChartEventSetting } from './utils/buildInitialChartEventSetting';
import {
  buildEventClusterCacheKey,
  getCachedEventClusterData,
  setCachedEventClusterData,
} from './utils/eventClusterSessionCache';
import {
  loadOpinionChartEventSetting,
  saveOpinionChartEventSetting,
} from './utils/opinionChartEventSettingStorage';
import {
  EMPTY_EVENT_DATA,
  hasBubbleEventContent,
  resolveInitialOpinionChartEventState,
} from './utils/resolveInitialOpinionChartEventState';
import type {
  IChangeOpinionChartEventSettingOptions,
  IOpinionChartEventData,
  IOpinionChartEventSetting,
  IUseOpinionChartEventsParams,
  IUseOpinionChartEventsResult,
} from './types';

/**
 * 情报侧事件 Hook：请求 event_cluster 并转换为图表事件结构
 */
export function useOpinionChartEvents(
  params: IUseOpinionChartEventsParams,
): IUseOpinionChartEventsResult {
  const {
    gameIds,
    startTime,
    endTime,
    dateType,
    settingStorageKey,
    enabled = true,
    needEventData = true,
  } = params;

  const [eventData, setEventData] = useState<IOpinionChartEventData>(() =>
    resolveInitialOpinionChartEventState(params).eventData,
  );
  const [chartEventSetting, setChartEventSetting] =
    useState<IOpinionChartEventSetting>(() =>
      resolveInitialOpinionChartEventState(params).chartEventSetting,
    );
  const [error, setError] = useState(false);
  const isChangeRef = useRef(false);
  const eventDataRef = useRef(eventData);
  const { loading, wrapperRequest } = useCancelRequestHook();

  eventDataRef.current = eventData;

  const requestParams = useMemo(() => {
    if (
      !enabled ||
      !needEventData ||
      !gameIds?.length ||
      !startTime ||
      !endTime
    ) {
      return null;
    }

    return buildEventClusterParams({
      gameIds,
      startTime,
      endTime,
      dateType,
    });
  }, [enabled, needEventData, gameIds, startTime, endTime, dateType]);

  function persistEventSetting(
    data: IOpinionChartEventSetting,
    currentEventData: IOpinionChartEventData = eventDataRef.current,
  ) {
    if (!settingStorageKey || currentEventData.isEmpty) {
      return;
    }

    saveOpinionChartEventSetting(settingStorageKey, {
      showEventAxis: data.showEventAxis,
      showEventBubbles: data.showEventBubbles,
      selectedEventKeys: buildEventSettingSelectValue(
        currentEventData.eventList,
        data.bubbleEvent,
      ),
      isCustomized: true,
    });
  }

  function changeEventSetting(
    data: IOpinionChartEventSetting,
    options?: IChangeOpinionChartEventSettingOptions,
  ) {
    if (
      data.showEventAxis !== chartEventSetting.showEventAxis ||
      data.showEventBubbles !== chartEventSetting.showEventBubbles
    ) {
      isChangeRef.current = true;
    }

    if (options?.persist) {
      persistEventSetting(data);
    }

    setChartEventSetting(data);
  }

  function requestEvents(options?: { silent?: boolean }) {
    if (!requestParams) {
      return;
    }

    const silent =
      options?.silent ?? !eventDataRef.current.isEmpty;

    setError(false);
    wrapperRequest(
      cancelFunc =>
        fetchEventCluster(requestParams, cancelFunc)
          .then(response => {
            const nextEventData = mapEventClusterListToOpinionEventData(
              response?.list || [],
              dateType,
            );
            const haveEventList = !nextEventData.isEmpty;
            const cacheKey = buildEventClusterCacheKey({
              ...requestParams,
              date_type: dateType,
            });

            setCachedEventClusterData(cacheKey, nextEventData);
            setEventData(nextEventData);
            setChartEventSetting(prev => {
              const nextBaseSetting: IOpinionChartEventSetting = {
                ...prev,
                showEventAxis: isChangeRef.current
                  ? prev.showEventAxis
                  : haveEventList,
              };

              if (hasBubbleEventContent(prev.bubbleEvent)) {
                return nextBaseSetting;
              }

              return buildInitialChartEventSetting({
                eventData: nextEventData,
                currentSetting: nextBaseSetting,
                persistedSetting: loadOpinionChartEventSetting(settingStorageKey),
                dateType,
              });
            });
          })
          .catch(() => {
            setError(true);
            setEventData(EMPTY_EVENT_DATA);
          }),
      silent,
    );
  }

  useEffect(() => {
    if (!requestParams) {
      setEventData(EMPTY_EVENT_DATA);
      setChartEventSetting(prev => ({
        ...prev,
        bubbleEvent: {},
      }));
      return;
    }

    const cacheKey = buildEventClusterCacheKey({
      ...requestParams,
      date_type: dateType,
    });
    const cachedEventData = getCachedEventClusterData(cacheKey);
    const hasCachedEventData = !!cachedEventData && !cachedEventData.isEmpty;

    if (hasCachedEventData) {
      setEventData(cachedEventData);
      setChartEventSetting(prev => {
        if (hasBubbleEventContent(prev.bubbleEvent)) {
          return prev;
        }

        return buildInitialChartEventSetting({
          eventData: cachedEventData,
          currentSetting: prev,
          persistedSetting: loadOpinionChartEventSetting(settingStorageKey),
          dateType,
        });
      });
    } else {
      setEventData(EMPTY_EVENT_DATA);
      setChartEventSetting(prev => ({
        ...prev,
        bubbleEvent: {},
      }));
    }

    requestEvents({ silent: hasCachedEventData });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestParams, dateType]);

  return {
    eventData,
    chartEventSetting,
    changeEventSetting,
    settingStorageKey,
    loading,
    error,
    refetch: requestEvents,
  };
}
